/**
 * DoiChieuBangKePage — đối chiếu bảng kê .xlsx của kế toán với hệ thống.
 *
 * Màn này CỐ Ý không có nút nào ghi dữ liệu, và đó là điều quan trọng nhất về
 * nó. Bảng kê chỉ có số hoá đơn, ngày, tên người bán và tổng tiền — không mã
 * số thuế, không ký hiệu. Đo trên file mẫu thật: 17/21 dòng đã có sẵn trong hệ
 * thống với đầy đủ định danh và đã gán giáo viên. Một nút "nhập bảng kê" sẽ đẻ
 * ra 17 bản ghi nghèo hơn hẳn bản nhà cung cấp đã đồng bộ về, mà những bản ghi
 * đó dùng để chi tiền cho giáo viên.
 *
 * Nên việc của màn này là trả lời ba câu hỏi, không phải tạo gì:
 *   - dòng nào ĐÃ CÓ,
 *   - dòng nào CHƯA CÓ (đi đường chụp ảnh ở /admin/meinvoice/ocr, nơi có đủ
 *     định danh để chống trùng),
 *   - dòng nào có rồi nhưng hai bên GHI KHÁC NHAU — đây mới là thứ đáng tiền
 *     của việc đối chiếu.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form, ProgressBar, Row, Spinner, Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import invoiceOcrService from '../services/invoiceOcr';
import AdminLogoutButton from '../components/AdminLogoutButton';

const TRANG_THAI = {
  matched: { text: 'Đã có', bg: 'success' },
  ambiguous: { text: 'Khớp nhiều bản', bg: 'warning' },
  missing: { text: 'Chưa có', bg: 'danger' },
};

const money = (n) => (typeof n === 'number' ? n.toLocaleString('vi-VN') : '—');

/**
 * Ngày của bản ghi hệ thống về dạng người đọc.
 *
 * `InvoiceDate` tới đây là chuỗi ISO mà backend đã tính theo giờ Việt Nam khi
 * so sánh, nhưng chuỗi thô vẫn là UTC. Cắt 10 ký tự đầu ở đây sẽ LÙI MỘT NGÀY
 * đúng như lỗi backend đã mắc — nên đọc lại theo `Asia/Ho_Chi_Minh`.
 */
const ngayHeThong = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Ho_Chi_Minh' });
};

export default function DoiChieuBangKePage() {
  const [dangChay, setDangChay] = useState(false);
  const [pct, setPct] = useState(0);
  const [bc, setBc] = useState(null);
  const [chiLech, setChiLech] = useState(false);
  const [dangTao, setDangTao] = useState(false);
  const fileRef = useRef(null);

  const chay = useCallback(async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Chưa chọn file bảng kê');
    setDangChay(true);
    setPct(0);
    setBc(null);
    try {
      const res = await invoiceOcrService.doiChieuXlsx(file, {
        onUploadProgress: (e) => { if (e.total) setPct(Math.round((e.loaded * 100) / e.total)); },
      });
      setBc(res?.data || null);
      toast.success('Đối chiếu xong');
    } catch (e) {
      // Backend trả kèm danh sách cột còn thiếu — nói tên cột cho kế toán sửa
      // được ngay trên file của họ, thay vì "file không hợp lệ".
      const d = e?.response?.data;
      const thieu = d?.data?.missingColumns || [];
      toast.error(thieu.length
        ? `${d?.message} Cột cần có: ${thieu.join(', ')}.`
        : (d?.message || e.message));
    } finally {
      setDangChay(false);
      setPct(0);
    }
    return undefined;
  }, []);

  /**
   * Tạo các dòng còn thiếu. Chỉ hiện khi file có đủ định danh — xem khối lưu ý
   * bên dưới để biết vì sao.
   */
  const tao = useCallback(async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Chọn lại file bảng kê rồi bấm Đối chiếu trước');
    const n = bc?.tong?.thieu || 0;
    if (!window.confirm(
      `Tạo ${n} hoá đơn còn thiếu?\n\n`
      + 'Hoá đơn tạo từ bảng kê không có phần thuế và dòng hàng, và sẽ bị CHẶN khỏi '
      + 'lệnh chi tiền cho tới khi có người mở.\n\n'
      + 'Dòng đã có trong hệ thống sẽ KHÔNG bị đụng tới.',
    )) return undefined;

    setDangTao(true);
    try {
      const res = await invoiceOcrService.taoThieuXlsx(file);
      const kq = res?.data?.data || res?.data;
      toast.success(`Đã tạo ${kq?.daTao?.length ?? 0} hoá đơn`);
      if (kq?.boQua?.length) {
        toast.warn(`${kq.boQua.length} dòng bỏ qua: ${kq.boQua[0].vi}`);
      }
      await chay();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDangTao(false);
    }
    return undefined;
  }, [bc, chay]);

  const rows = useMemo(() => {
    const ds = bc?.rows || [];
    return chiLech ? ds.filter((r) => r.lechs.length || r.status !== 'matched') : ds;
  }, [bc, chiLech]);

  return (
    <Container fluid className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <h5 className='mb-0'>Đối chiếu bảng kê nhiên liệu</h5>
        <div className='d-flex gap-2'>
          <Button size='sm' variant='outline-secondary' href='/admin/meinvoice'>Hoá đơn</Button>
          <Button size='sm' variant='outline-secondary' href='/admin/meinvoice/ocr'>Nhập bằng AI</Button>
          <AdminLogoutButton size='sm' />
        </div>
      </div>

      <Card className='mb-3'>
        <Card.Body>
          <Row className='g-2 align-items-end'>
            <Col md={8}>
              <Form.Label className='fw-semibold mb-1'>File bảng kê (.xlsx)</Form.Label>
              <Form.Control ref={fileRef} type='file' accept='.xlsx,.xls' />
              <Form.Text className='text-muted'>
                Cần có các cột: Ngày xuất hoá đơn, Số hoá đơn, Tổng tiền. Thứ tự cột không
                quan trọng.
              </Form.Text>
            </Col>
            <Col md={4} className='text-md-end'>
              <Button onClick={chay} disabled={dangChay}>
                {dangChay ? <Spinner size='sm' animation='border' className='me-2' /> : null}
                Đối chiếu
              </Button>
            </Col>
          </Row>
          {dangChay && pct > 0 ? <ProgressBar now={pct} label={`${pct}%`} className='mt-2' /> : null}
        </Card.Body>
      </Card>

      {bc ? (
        <>
          <Alert variant='light' className='border'>
            <div className='d-flex flex-wrap gap-3 align-items-center'>
              <span><strong>{bc.rows.length}</strong> dòng · sheet “{bc.sheetName}”</span>
              <span><Badge bg='success'>{bc.tong.khop}</Badge> đã có</span>
              <span><Badge bg='warning' text='dark'>{bc.tong.mo}</Badge> khớp nhiều bản</span>
              <span><Badge bg='danger'>{bc.tong.thieu}</Badge> chưa có</span>
              <span><Badge bg='secondary'>{bc.tong.lech}</Badge> lệch dữ liệu</span>
              <Form.Check
                type='switch'
                id='chi-lech'
                className='ms-auto'
                label='Chỉ hiện dòng cần xử lý'
                checked={chiLech}
                onChange={(e) => setChiLech(e.target.checked)}
              />
            </div>
          </Alert>

          {/* Nói thẳng vì sao không có nút nhập — nếu không, người dùng sẽ đi
            * tìm nó, rồi tự tạo tay 4 dòng thiếu mà không có mã số thuế. */}
          {bc.hasInvoiceIdentity ? (
            <Alert variant='success' className='py-2 d-flex flex-wrap align-items-center gap-2'>
              <span>
                Bảng kê này có đủ <strong>Ký hiệu</strong> và <strong>Mã số thuế người bán</strong>,
                nên tạo được các dòng còn thiếu. Dòng đã có sẽ không bị đụng tới.
              </span>
              <Button
                size='sm'
                variant='success'
                className='ms-auto'
                disabled={dangTao || !bc.tong.thieu}
                onClick={tao}
              >
                {dangTao ? <Spinner size='sm' animation='border' className='me-2' /> : null}
                Tạo {bc.tong.thieu} hoá đơn còn thiếu
              </Button>
            </Alert>
          ) : (
            <Alert variant='info' className='py-2'>
              Bảng kê này <strong>không có</strong> cột Ký hiệu và Mã số thuế người bán nên
              chưa tạo hoá đơn từ đây được — thiếu chúng thì khoá chống trùng sai, và khi
              nhà cung cấp đồng bộ bản thật về sẽ thành hai bản ghi cho một hoá đơn.
              <div className='mt-1'>
                Hai cách: thêm hai cột đó vào file rồi đối chiếu lại, hoặc chụp ảnh hoá đơn
                rồi nhập ở <a href='/admin/meinvoice/ocr'>Nhập hoá đơn bằng AI</a>.
              </div>
            </Alert>
          )}

          <Card>
            <div className='table-responsive' style={{ maxHeight: '65vh', overflow: 'auto' }}>
              <Table hover size='sm' className='mb-0 align-middle'>
                <thead className='table-light' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Dòng</th>
                    <th>Số HĐ</th>
                    <th>Ngày (bảng kê)</th>
                    <th>Tổng tiền (bảng kê)</th>
                    <th>Người bán (bảng kê)</th>
                    <th>Trạng thái</th>
                    <th>Trong hệ thống</th>
                    <th>Chênh lệch</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const tt = TRANG_THAI[r.status] || TRANG_THAI.missing;
                    return (
                      <tr key={`${r.invoiceNo}-${r.excelRow}`}>
                        <td className='text-muted'>{r.excelRow}</td>
                        <td className='fw-semibold'>{r.invoiceNo}</td>
                        <td>{r.invoiceDate || '—'}</td>
                        <td className='text-end'>{money(r.totalAmount)}</td>
                        <td style={{ maxWidth: 280 }} className='text-truncate' title={r.sellerName}>
                          {r.sellerName || '—'}
                        </td>
                        <td><Badge bg={tt.bg} text={tt.bg === 'warning' ? 'dark' : undefined}>{tt.text}</Badge></td>
                        <td style={{ minWidth: 220 }}>
                          {r.invoice ? (
                            <div className='small'>
                              {/* Ký hiệu và MST là đúng phần hệ thống biết mà tờ
                                * giấy không biết — nên chúng đứng ở đây. */}
                              <div>{r.invoice.Series} · {ngayHeThong(r.invoice.InvoiceDate)}</div>
                              <div className='text-muted'>
                                MST {r.invoice.SellerTaxCode || '—'} · {money(r.invoice.TotalAmount)}
                                {r.invoice.DrivingTeacher ? ` · GV ${r.invoice.DrivingTeacher}` : ''}
                              </div>
                            </div>
                          ) : (
                            <span className='text-muted small'>
                              {r.candidates.length ? `${r.candidates.length} bản ghi cùng số — cần người chọn` : '—'}
                            </span>
                          )}
                        </td>
                        <td className='small text-danger' style={{ maxWidth: 260 }}>
                          {r.lechs.length ? r.lechs.join(' · ') : ''}
                        </td>
                      </tr>
                    );
                  })}
                  {!rows.length ? (
                    <tr>
                      <td colSpan={8} className='text-center text-muted py-4'>
                        Không có dòng nào cần xử lý.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      ) : null}
    </Container>
  );
}
